import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { PasswordInput } from '../../Shared/Component/forms/PasswordInput';
import { useFormController } from '../../Shared/Component/readFormData/useFormController';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';
import { getPasswordValidationError } from '../../Shared/Utils/validationPolicy';
import { useValidationPolicy } from '../../Shared/Hooks/useValidationPolicy';
import { PasswordPolicyHints } from '../../Shared/Component/forms/PasswordPolicyHints';
import { resetPassword } from '../Services/UserService';
import { useRedirect } from '../../Shared/Hooks/useRedirect ';

function ResetPasswordComponent() {
  const location = useLocation();
  const redirect = useRedirect();
  const token = new URLSearchParams(location.search).get("token") || "";

  const {
    values,
    handleInputChange,
  } = useFormController<{ password: string; confirmPassword: string }>({
    password: "",
    confirmPassword: ""
  });

  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [loading, setLoading] = useState(false);
  const { policy } = useValidationPolicy();
  const redirectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!token) {
      setAlert({
        variant: "error",
        title: "Invalid Reset Link",
        message: "This password reset link is missing or invalid. Please request a new one.",
        onClose: () => setAlert(null),
      });
    }
    return () => {
      if (redirectTimer.current) clearTimeout(redirectTimer.current);
    };
  }, [token]);

  const handleSubmit = async () => {
    if (!token) {
      return;
    }

    const passwordError = getPasswordValidationError(values.password, policy);
    if (passwordError) {
      setAlert({
        variant: "error",
        title: "Weak Password",
        message: passwordError,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }

    if (values.password !== values.confirmPassword) {
      setAlert({
        variant: "error",
        title: "Password Mismatch",
        message: "Both passwords must match.",
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      return;
    }

    setLoading(true);
    setAlert(null);

    const response = await resetPassword({ token, password: values.password });

    if (response.success) {
      setAlert({
        variant: "success",
        title: "Password Reset",
        message: response.data?.message || response.message,
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      redirectTimer.current = setTimeout(() => redirect("/login"), 3000);
    } else {
      setAlert({
        variant: "error",
        title: "Reset Error",
        message: response.message,
        autoCloseMs: 6000,
        onClose: () => setAlert(null),
      });
    }

    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Alert Section */}
      <div className="mb-4">
        {alert && (
          <Alert
            variant={alert.variant}
            title={alert.title}
            message={alert.message}
            onClose={() => setAlert(null)}
          />
        )}
      </div>
      {/* Decorative colorful background glows for that vibrant cyberpunk feel */}
      <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-1/4 right-1/4 w-72 h-72 bg-fuchsia-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md z-10">
        <h2 className="mt-6 text-center text-3xl font-extrabold bg-gradient-to-r from-cyan-400 via-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">
          Set a new password
        </h2>
        <p className="mt-2 text-center text-sm text-slate-400 font-medium tracking-wide">
          Choose a strong password for your account. Your existing sessions
          will be signed out.
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md z-10">
        <div className="bg-slate-900/80 backdrop-blur-xl py-8 px-4 shadow-2xl shadow-cyan-950/20 border border-slate-800 rounded-xl sm:px-10 relative before:absolute before:inset-0 before:p-[1px] before:bg-gradient-to-b before:from-slate-700 before:to-transparent before:rounded-xl before:-z-10">

          <div className="space-y-6">
            <div>
              <PasswordInput
                label="New Password"
                name="password"
                placeholder="••••••••"
                value={values.password}
                onChange={handleInputChange("password")}
                className="text-slate-200 focus:border-cyan-400 focus:ring-cyan-400/20"
              />
              <PasswordPolicyHints password={values.password} policy={policy} />
            </div>

            <div>
              <PasswordInput
                label="Confirm New Password"
                name="confirmPassword"
                placeholder="••••••••"
                value={values.confirmPassword}
                onChange={handleInputChange("confirmPassword")}
                className="text-slate-200 focus:border-cyan-400 focus:ring-cyan-400/20"
              />
            </div>

            <div>
              <button
                onClick={handleSubmit}
                disabled={loading || !token}
                className={`w-full flex justify-center py-2.5 px-4 border border-transparent rounded-md shadow-lg shadow-cyan-500/20 text-sm font-semibold text-slate-950 bg-gradient-to-r from-cyan-400 via-indigo-400 to-fuchsia-400 hover:from-cyan-300 hover:via-indigo-300 hover:to-fuchsia-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-cyan-400 transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0 ${
                  loading || !token ? "opacity-70 cursor-not-allowed" : ""
                }`}
              >
                {loading ? "Resetting password..." : "Reset Password"}
              </button>
            </div>

            <div className="text-center">
              <Link
                to="/forgot-password"
                className="text-sm text-cyan-400 hover:text-cyan-300 font-medium tracking-wide"
              >
                Request a new link
              </Link>
            </div>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-800"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-3 bg-slate-900 text-cyan-400/80 font-mono tracking-wider text-xs uppercase">
                  Secure Encrypted Reset
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ResetPasswordComponent;
